/**
 * InterestSection Component
 * 
 * Combines InterestToggle, rate input, and FrequencySelector
 * Displays real-time calculated interest and total amount
 * Includes minimum interest period for early repayment protection
 */
import React, { useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import InterestToggle from './InterestToggle';
import FrequencySelector from './FrequencySelector';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { calculateInterest, calculateTotalAmount } from '../utils/interestCalculator';

const InterestSection = ({
  template,
  enabled,
  onEnabledChange,
  rate,
  onRateChange,
  frequency,
  onFrequencyChange,
  minimumInterestMonths = 3,
  onMinimumInterestMonthsChange,
  principal,
  duration,
  currency = 'INR',
  disabled = false,
  className = ''
}) => {
  const { t, formatCurrency, getTemplateFieldLabel } = useLanguage();

  // Calculate full-duration interest
  const calculatedInterest = useMemo(() => {
    if (!enabled || !principal || !rate || !duration || !frequency) {
      return 0;
    }
    return calculateInterest(principal, rate, duration, frequency);
  }, [enabled, principal, rate, duration, frequency]);

  const totalAmount = useMemo(() => {
    if (!principal) {
      return 0;
    }
    if (!enabled) {
      return principal;
    }
    return calculateTotalAmount(principal, rate, duration, frequency);
  }, [enabled, principal, rate, duration, frequency]);

  // Calculate minimum interest
  const minimumInterest = useMemo(() => {
    if (!enabled || !principal || !rate || !minimumInterestMonths) {
      return 0;
    }
    const effectiveMinMonths = Math.min(minimumInterestMonths, duration || 1);
    return calculateInterest(principal, rate, effectiveMinMonths, frequency);
  }, [enabled, principal, rate, minimumInterestMonths, duration, frequency]);

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">
          {t('interest.interest_configuration')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Interest Toggle */}
        <InterestToggle
          enabled={enabled}
          onChange={onEnabledChange}
          disabled={disabled}
        />

        {/* Interest Configuration Fields - Only shown when enabled */}
        {enabled && (
          <div className="space-y-4 pt-2">
            {/* Interest Rate Input */}
            <div className="space-y-2">
              <Label htmlFor="interest-rate">
                {getTemplateFieldLabel(template, 'interest')}
              </Label>
              <Input
                id="interest-rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={rate || ''}
                onChange={(e) => onRateChange(parseFloat(e.target.value) || 0)}
                placeholder={getTemplateFieldLabel(template, 'interest')}
                disabled={disabled}
              />
            </div>

            {/* Frequency Selector */}
            <FrequencySelector
              value={frequency}
              onChange={onFrequencyChange}
              disabled={disabled}
            />

            {/* Minimum Interest Period */}
            {onMinimumInterestMonthsChange && (
              <div className="space-y-2">
                <Label htmlFor="minimum-interest-months">
                  {t('interest.minimum_interest_period') || 'Minimum Interest Period (months)'}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('interest.minimum_interest_help') || 'Minimum months of interest charged even on early repayment'}
                </p>
                <Input
                  id="minimum-interest-months"
                  type="number"
                  min="0"
                  max={duration || 360}
                  step="1"
                  value={minimumInterestMonths}
                  onChange={(e) => onMinimumInterestMonthsChange(parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-32"
                />
              </div>
            )}

            {/* Calculated Values Display */}
            {principal > 0 && rate > 0 && duration > 0 && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {t('interest.full_duration_interest') || 'Interest (full duration)'}:
                  </span>
                  <span className="font-medium">
                    {formatCurrency(calculatedInterest, currency)}
                  </span>
                </div>
                {minimumInterestMonths > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {t('interest.minimum_interest') || `Min. interest (${minimumInterestMonths} mo)`}:
                    </span>
                    <span className="font-medium text-amber-600">
                      {formatCurrency(minimumInterest, currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {t('interest.total_with_interest')}:
                  </span>
                  <span className="text-lg font-bold">
                    {formatCurrency(totalAmount, currency)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Display total when interest is disabled */}
        {!enabled && principal > 0 && (
          <div className="pt-2 border-t">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {t('loan.detail.total_amount')}:
              </span>
              <span className="text-lg font-bold">
                {formatCurrency(principal, currency)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InterestSection;
